import { useMemo, useRef, useState } from 'react'
import { useScrollLock } from '@/hooks/useScrollLock'
import { useIntl } from 'react-intl'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/store'
import {
	selectSelectedOrderType,
	selectIsDineIn,
	selectIsDeferred,
	selectExpectedTimeUtc,
	selectCartItemCount,
	selectCartTotal,
	selectDineInTableName,
	selectNonDineInOrderTypeCount,
	selectDistanceLeadTimeInMinutes,
	selectHasFutureSlotsForTakeout,
	selectHasFutureSlotsForDelivery,
} from '@/store/ticketSlice'
import { useLocationRef } from '@/contexts/RefContext'
import { PATHS } from '@/router/paths'
import { formatPrice } from '@/lib/formatPrice'
import { convertMinutesToHoursAndMinutes, getAsapLeadTimeMinutes, getLocale } from '@/lib/timeUtils'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatScheduledTime } from '@/lib/formatScheduledTime'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import { OrderDetailsPanel } from './OrderDetailsPanel'
import {
	BottomBarOverlay,
	BottomBarCard,
	BottomBarInner,
	BottomBarDetailsRow,
	BottomBarDetailsPairs,
	BottomBarDetailPair,
	BottomBarTimingPair,
	BottomBarOrderTypeLabel,
	BottomBarTimingText,
	BottomBarTimingBold,
	BottomBarEditButton,
	BottomBarCheckoutRow,
	BottomBarCheckoutButton,
	BottomBarItemBadge,
	drawerPaperSx,
	DrawerPuller,
	DrawerHeader,
	DrawerHeading,
	DrawerHeadingCount,
	DrawerCloseButton,
	DrawerBody,
} from './CartBottomBar.styled'

interface CartBottomBarProps {
	scheduleId: string
	timeZoneOffset?: number
	currency?: string
	countryCode?: string
}

export function CartBottomBar({
	scheduleId,
	timeZoneOffset,
	currency = 'USD',
	countryCode = 'US',
}: CartBottomBarProps) {
	const intl = useIntl()
	const { t } = useFormatMessage()
	const navigate = useNavigate()
	const locationRef = useLocationRef()

	const orderType = useAppSelector(selectSelectedOrderType)
	const isDineIn = useAppSelector(selectIsDineIn)
	const isDeferred = useAppSelector(selectIsDeferred)
	const expectedTimeUtc = useAppSelector(selectExpectedTimeUtc)
	const itemCount = useAppSelector(selectCartItemCount)
	const cartTotal = useAppSelector(selectCartTotal)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const nonDineInOrderTypeCount = useAppSelector(selectNonDineInOrderTypeCount)
	const distanceLeadTimeInMinutes = useAppSelector(selectDistanceLeadTimeInMinutes)
	const hasFutureSlotsForTakeout = useAppSelector(selectHasFutureSlotsForTakeout)
	const hasFutureSlotsForDelivery = useAppSelector(selectHasFutureSlotsForDelivery)
	// === false, not falsiness: null means the time-slots fetch failed or has not completed and
	// future slots may still exist, so only a confirmed empty result hides the edit affordance.
	const isTakeoutAsapOnly =
		orderType?.ref === 'TAKEOUT' &&
		hasFutureSlotsForTakeout === false &&
		nonDineInOrderTypeCount === 1
	// STO-3802: mirrors isTakeoutAsapOnly. Delivery ASAP with no future slots and no other
	// non-dine-in order type leaves nothing for the customer to change, so hide the edit affordance.
	const isDeliveryAsapOnly =
		orderType?.ref === 'DELIVERY' &&
		hasFutureSlotsForDelivery === false &&
		nonDineInOrderTypeCount === 1

	const { data: itemsData } = useMenuItems(locationRef, scheduleId)
	const menuItems = useMemo(() => {
		const map: Record<string, NonNullable<typeof itemsData>['items'][number]> = {}
		if (itemsData) {
			for (const item of itemsData.items) {
				map[item.ref] = item
			}
		}
		return map
	}, [itemsData])

	const [drawerOpen, setDrawerOpen] = useState(false)
	const triggerButtonRef = useRef<HTMLButtonElement>(null)

	useScrollLock(drawerOpen)

	const isCartEmpty = itemCount === 0

	const orderTypeName = orderType?.name ?? t('component:cartActionsFooter:orderType:fallback')

	// PO demo parity: DineIn shows table name in place of ASAP timing; no edit.
	const timingLabel = isDineIn
		? { bold: dineInTableName ?? '', regular: '' }
		: (() => {
				if (isDeferred && expectedTimeUtc) {
					return {
						bold: '',
						regular: formatScheduledTime(
							expectedTimeUtc,
							getLocale(countryCode),
							t('orderSettings:todayLabel'),
							timeZoneOffset,
							t('orderSettings:tomorrowLabel'),
							t('orderSettings:scheduledTimeAt'),
						),
					}
				}

				const leadMins = orderType
					? getAsapLeadTimeMinutes(orderType, distanceLeadTimeInMinutes)
					: null
				const duration =
					leadMins != null
						? `${intl.formatMessage({ id: 'orderSettings:asapSubtitleAbout' })} ${convertMinutesToHoursAndMinutes(leadMins)}`
						: intl.formatMessage({ id: 'orderSettings:asapSubtitleFallback' })
				return { bold: t('orderSettings:asapLabel'), regular: ` ${duration}` }
			})()

	const formattedTotal = formatPrice(cartTotal, intl.locale, currency)

	function handleEditOrderSettings() {
		void navigate(`${PATHS.orderSettings(locationRef)}?edit=1`)
	}

	function handleReviewOrder() {
		if (isCartEmpty) return
		setDrawerOpen(true)
	}

	function handleCloseDrawer() {
		setDrawerOpen(false)
		// Return focus to the trigger so keyboard users don't lose their position.
		// SwipeableDrawer handles Escape and scroll lock internally via its Modal wrapper.
		triggerButtonRef.current?.focus()
	}

	return (
		<>
			<BottomBarOverlay>
				<BottomBarCard>
					<BottomBarInner>
						<BottomBarDetailsRow>
							<BottomBarDetailsPairs>
								<BottomBarDetailPair>
									<ShoppingBagOutlinedIcon
										sx={{ fontSize: 20, flexShrink: 0 }}
										aria-hidden="true"
									/>
									<BottomBarOrderTypeLabel>
										{orderTypeName}
									</BottomBarOrderTypeLabel>
								</BottomBarDetailPair>

								{(timingLabel.bold || timingLabel.regular) && (
									<BottomBarTimingPair>
										<AccessTimeIcon
											sx={{ fontSize: 20, flexShrink: 0 }}
											aria-hidden="true"
										/>
										<BottomBarTimingText
											title={
												`${timingLabel.bold}${timingLabel.regular}`.trim() ||
												undefined
											}
										>
											{timingLabel.bold ? (
												<BottomBarTimingBold>
													{timingLabel.bold}
												</BottomBarTimingBold>
											) : null}
											{timingLabel.regular}
										</BottomBarTimingText>
									</BottomBarTimingPair>
								)}
							</BottomBarDetailsPairs>

							{/* PO demo parity: no edit for DineIn sessions or TAKEOUT ASAP-only — nothing to configure. */}
							{!isDineIn && !isTakeoutAsapOnly && !isDeliveryAsapOnly && (
								<BottomBarEditButton
									onClick={handleEditOrderSettings}
									aria-label={t(
										'component:cartActionsFooter:editOrderSettings:label',
									)}
								>
									<EditOutlinedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
								</BottomBarEditButton>
							)}
						</BottomBarDetailsRow>

						{/* Review Order button — opens the order details drawer.
						    Badge shows item count inside the button (Figma 562:40895). */}
						<BottomBarCheckoutRow>
							<BottomBarCheckoutButton
								ref={triggerButtonRef}
								isDisabled={isCartEmpty}
								disabled={isCartEmpty}
								onClick={handleReviewOrder}
								aria-label={t('component:cartBottomBar:reviewOrder:ariaLabel', {
									total: formattedTotal,
								})}
								aria-haspopup="dialog"
							>
								{t('component:cartBottomBar:reviewOrder:label', {
									total: formattedTotal,
								})}
								{!isCartEmpty && (
									<BottomBarItemBadge aria-hidden="true">
										{itemCount}
									</BottomBarItemBadge>
								)}
							</BottomBarCheckoutButton>
						</BottomBarCheckoutRow>
					</BottomBarInner>
				</BottomBarCard>
			</BottomBarOverlay>

			<SwipeableDrawer
				anchor="bottom"
				open={drawerOpen}
				onOpen={() => {}}
				onClose={handleCloseDrawer}
				slotProps={{
					paper: {
						sx: drawerPaperSx,
						'aria-label': t('component:orderDetailsPanel:heading:label'),
					},
				}}
				disableSwipeToOpen
			>
				<DrawerPuller aria-hidden="true" />
				<DrawerHeader>
					<DrawerHeading>
						{t('component:orderDetailsPanel:heading:label')}{' '}
						<DrawerHeadingCount>({itemCount})</DrawerHeadingCount>
					</DrawerHeading>
					<DrawerCloseButton
						onClick={handleCloseDrawer}
						aria-label={t('component:cartBottomBar:drawer:close:label')}
					>
						<CloseIcon fontSize="small" />
					</DrawerCloseButton>
				</DrawerHeader>
				<DrawerBody>
					{/* Gate: do not render until menu items query resolves — Ember parity */}
					{itemsData !== undefined && (
						<OrderDetailsPanel
							menuItems={menuItems}
							hideHeader
							timeZoneOffset={timeZoneOffset}
							currency={currency}
							countryCode={countryCode}
						/>
					)}
				</DrawerBody>
			</SwipeableDrawer>
		</>
	)
}
